import type { TFunction } from "i18next";
import {
  AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
  ATTACHMENT_MAX_FILE_SIZE_MB,
} from "@/constants";
import { FileStorageRequestError } from "@/lib/file-storage/service";

/** Klasa błędu presign/complete/complete-delete skanów, na którą UI reaguje osobno. */
export type PaperScanErrorKind =
  /** umowa poza strukturą albo nie istnieje (404 bez kodu) */
  | "not_found"
  /** `paper_scan_not_found`: skan zniknął (np. usunięty w innej karcie) */
  | "scan_not_found"
  | "forbidden"
  | "limit_reached"
  | "not_foreign"
  | "not_active"
  | "not_pending"
  | "not_uploaded"
  | "too_large"
  | "unsupported_type"
  | "presign_failed"
  /** `paper_scan_too_many_pending`: za dużo otwartych rezerwacji (presign bez complete) z ostatniej godziny */
  | "too_many_pending"
  | "other";

/** Stabilne kody z body odpowiedzi backendu (spec: `code` obok `message`). */
const KIND_BY_CODE: Readonly<Record<string, PaperScanErrorKind>> = {
  paper_scan_limit_reached: "limit_reached",
  paper_scan_not_foreign: "not_foreign",
  paper_scan_not_found: "scan_not_found",
  paper_scan_not_active: "not_active",
  paper_scan_not_pending: "not_pending",
  paper_scan_not_uploaded: "not_uploaded",
  paper_scan_too_large: "too_large",
  paper_scan_unsupported_type: "unsupported_type",
  paper_scan_presign_failed: "presign_failed",
  paper_scan_too_many_pending: "too_many_pending",
};

/**
 * Rozpoznaje błąd z endpointów skanów: najpierw po stabilnym `code` z body, potem po statusie HTTP.
 * Fallback po treści `message` zostaje dla odpowiedzi bez `code` (np. z bramki API).
 */
export function classifyPaperScanError(error: unknown): PaperScanErrorKind {
  if (!(error instanceof FileStorageRequestError)) return "other";
  const byCode = error.apiCode ? KIND_BY_CODE[error.apiCode] : undefined;
  if (byCode) return byCode;
  switch (error.status) {
    case 404:
      return "not_found";
    case 403:
      return "forbidden";
    case 409:
      return /limit/i.test(error.apiMessage) ? "limit_reached" : "not_foreign";
    default:
      return "other";
  }
}

/** Błędy, po których strona jest nieświeża: serwer zna inny stan umowy albo skanów niż cache. */
const KINDS_REQUIRING_REFRESH: ReadonlySet<PaperScanErrorKind> =
  new Set<PaperScanErrorKind>([
    "not_found",
    "scan_not_found",
    "not_foreign",
    "limit_reached",
    "not_active",
    "not_pending",
  ]);

/** Czy po tym błędzie odświeżyć szczegóły umowy (lista ma pokazać stan serwera). */
export function shouldRefreshAgreementAfter(error: unknown): boolean {
  return KINDS_REQUIRING_REFRESH.has(classifyPaperScanError(error));
}

/**
 * Opis błędu do toastu: tłumaczenie dla każdego kodu skanów i statusów 403/404/409, ogólny tłumaczony
 * tekst dla reszty. `message` backendu i wyjątków sieci są po angielsku, więc nie trafiają do UI
 * (surowy błąd loguje hook uploadu).
 */
export function describePaperScanError(error: unknown, t: TFunction): string {
  switch (classifyPaperScanError(error)) {
    case "not_found":
      return t("agreements.errors.paperConfirmationNotFound");
    case "scan_not_found":
      return t("agreements.paperScans.errors.scanNotFound");
    case "forbidden":
      return t("errors.403");
    case "limit_reached":
      return t("agreements.paperScans.limitReached", {
        maxFiles: AGREEMENT_PAPER_SCANS_MAX_ACTIVE,
      });
    case "not_foreign":
      return t("agreements.paperScans.notForeign");
    case "not_active":
      return t("agreements.paperScans.errors.notActive");
    case "not_pending":
      return t("agreements.paperScans.errors.notPending");
    case "not_uploaded":
      return t("agreements.paperScans.errors.notUploaded");
    case "too_large":
      return t("agreements.paperScans.errors.tooLarge", {
        maxSize: ATTACHMENT_MAX_FILE_SIZE_MB,
      });
    case "unsupported_type":
      return t("agreements.paperScans.errors.unsupportedType");
    case "presign_failed":
      return t("agreements.paperScans.errors.presignFailed");
    case "too_many_pending":
      return t("agreements.paperScans.errors.tooManyPending");
    default:
      return t("agreements.paperScans.errors.generic");
  }
}
